import { useMemo } from 'react';
import { useKeycloakContext } from '../providers/keycloak-provider/auth-context';
import { createRouteGuards } from '../guards/route-guards';

function normalizeClinicIds(value: unknown): string[] | undefined {
  if (value == null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }

  if (typeof value === 'string') {
    if (value.includes('##')) {
      return value
        .split('##')
        .map((part) => part.trim())
        .filter(Boolean);
    }
    return value ? [value] : undefined;
  }

  return undefined;
}

const normalizeOrganizationIds = normalizeClinicIds;

/**
 * User information extracted from Keycloak token
 */
export interface UserInfo {
  /** User's unique identifier */
  id?: string;
  /** User's username */
  username?: string;
  /** User's email address */
  email?: string;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** User's full name (computed from first and last name) */
  fullName?: string;
  /** User's preferred username */
  preferredUsername?: string;
  /** User's practitioner ID from FHIR (from practitioner_id claim) */
  practitionerId?: string;
  /** User's organization ID from Keycloak (from organization_id claim) */
  organizationId?: string;
  /** User's accessible organization IDs (from organization_ids claim) */
  organizationIds?: string[];
  /** User's accessible clinic IDs (from clinic_ids claim) */
  clinicIds?: string[];
}

/**
 * Authentication state and methods
 */
export interface AuthState {
  /** Whether Keycloak has been initialized */
  isInitialized: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** User information (null if not authenticated) */
  user: UserInfo | null;
  /** User's realm roles */
  realmRoles: string[];
  /** User's resource roles */
  resourceRoles: string[];
  /** All user roles (realm + resource) */
  roles: string[];
  /** Check if user has a specific realm role */
  hasRole: (role: string) => boolean;
  /** Check if user has any of the specified roles */
  hasAnyRole: (roles: string[]) => boolean;
  /** Check if user has all of the specified roles */
  hasAllRoles: (roles: string[]) => boolean;
  /** Authentication guard function */
  requireAuthentication: () => void;
  /** Role-based authorization guard factory */
  requireRoles: (roles: string[]) => () => void;
  /** Login function */
  login: () => Promise<void>;
  /** Logout function */
  logout: () => Promise<void>;
  /** Get access token */
  getToken: () => string | undefined;
  /** Get access token, refreshing if about to expire */
  getValidToken: (minValidity?: number) => Promise<string | undefined>;
  /** Check if token is expired */
  isTokenExpired: () => boolean;
  /** Refresh token */
  updateToken: (minValidity?: number) => Promise<boolean>;
  /** Force token refresh and trigger re-render (use when Keycloak user attributes changed) */
  forceTokenRefresh: () => Promise<boolean>;
}

/**
 * Custom hook that provides comprehensive authentication state and methods.
 * This hook encapsulates all authentication-related functionality including
 * user information, roles, and auth guards.
 *
 * @returns Authentication state and methods
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthState {
  const {
    keycloak,
    initialized,
    authenticated,
    tokenVersion,
    forceTokenRefresh,
  } = useKeycloakContext();

  // Create route guards with the current keycloak instance
  const guards = useMemo(() => {
    if (!keycloak) {
      return {
        requireAuthentication: () => {
          throw new Error('Keycloak not initialized');
        },
        requireRoles: () => () => {
          throw new Error('Keycloak not initialized');
        },
      };
    }
    return createRouteGuards(keycloak);
  }, [keycloak]);

  // Extract user information from token
  // tokenVersion dependency ensures re-computation when token is force-refreshed
  const user = useMemo((): UserInfo | null => {
    if (!authenticated || !keycloak?.tokenParsed) {
      return null;
    }

    const token = keycloak.tokenParsed as any;

    const firstName = token.given_name || token.first_name;
    const lastName = token.family_name || token.last_name;

    return {
      id: token.sub || keycloak.idTokenParsed?.sub,
      username: token.preferred_username || token.username,
      email: token.email,
      firstName,
      lastName,
      fullName:
        firstName && lastName
          ? `${firstName} ${lastName}`
          : firstName || lastName,
      preferredUsername: token.preferred_username,
      practitionerId: token.practitioner_id,
      organizationId: token.organization_id,
      organizationIds: normalizeOrganizationIds(token.organization_ids),
      clinicIds: normalizeClinicIds(token.clinic_ids),
    };
  }, [authenticated, keycloak?.tokenParsed, tokenVersion]);

  // Extract roles
  const realmRoles = useMemo(() => {
    if (!authenticated || !keycloak) return [];
    return keycloak.realmAccess?.roles || [];
  }, [authenticated, keycloak?.realmAccess?.roles]);

  const resourceRoles = useMemo(() => {
    if (!authenticated || !keycloak?.resourceAccess) return [];

    const allResourceRoles: string[] = [];
    Object.values(keycloak.resourceAccess).forEach((resource) => {
      if (resource.roles) {
        allResourceRoles.push(...resource.roles);
      }
    });

    return allResourceRoles;
  }, [authenticated, keycloak?.resourceAccess]);

  const roles = useMemo(() => {
    return [...new Set([...realmRoles, ...resourceRoles])];
  }, [realmRoles, resourceRoles]);

  // Authorization helpers intentionally use realm roles only. Resource roles
  // belong to one client and must not widen product-level authorization.
  const hasRole = useMemo(() => {
    return (role: string): boolean => {
      if (!authenticated || !keycloak) return false;
      return keycloak.hasRealmRole(role);
    };
  }, [authenticated, keycloak]);

  const hasAnyRole = useMemo(() => {
    return (rolesToCheck: string[]): boolean => {
      return rolesToCheck.some((role) => hasRole(role));
    };
  }, [hasRole]);

  const hasAllRoles = useMemo(() => {
    return (rolesToCheck: string[]): boolean => {
      return rolesToCheck.every((role) => hasRole(role));
    };
  }, [hasRole]);

  // Authentication methods
  const login = useMemo(() => {
    return async (): Promise<void> => {
      if (!keycloak) {
        throw new Error('Keycloak not initialized');
      }
      await keycloak.login();
    };
  }, [keycloak]);

  const logout = useMemo(() => {
    return async (): Promise<void> => {
      if (!keycloak) {
        throw new Error('Keycloak not initialized');
      }
      await keycloak.logout();
    };
  }, [keycloak]);

  const getToken = useMemo(() => {
    return (): string | undefined => {
      return keycloak?.token;
    };
  }, [keycloak?.token]);

  const isTokenExpired = useMemo(() => {
    return (): boolean => {
      if (!keycloak) return true;
      return keycloak.isTokenExpired();
    };
  }, [keycloak]);

  const updateToken = useMemo(() => {
    return async (minValidity = 30): Promise<boolean> => {
      if (!keycloak) {
        throw new Error('Keycloak not initialized');
      }
      return await keycloak.updateToken(minValidity);
    };
  }, [keycloak]);

  const getValidToken = useMemo(() => {
    return async (minValidity = 30): Promise<string | undefined> => {
      if (!keycloak) return undefined;

      // Check if token is expired or will expire within minValidity seconds
      if (keycloak.isTokenExpired(minValidity)) {
        try {
          await keycloak.updateToken(minValidity);
        } catch (error) {
          // Token refresh failed - token is invalid, trigger re-login
          console.error('Failed to refresh token:', error);
          return undefined;
        }
      }

      return keycloak.token;
    };
  }, [keycloak]);

  return {
    isInitialized: initialized,
    isAuthenticated: authenticated,
    user,
    realmRoles,
    resourceRoles,
    roles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    requireAuthentication: guards.requireAuthentication,
    requireRoles: guards.requireRoles,
    login,
    logout,
    getToken,
    getValidToken,
    isTokenExpired,
    updateToken,
    forceTokenRefresh,
  };
}
